import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { applyFilters, categories } from "@/lib/films";
import type { FilterState } from "@/lib/films";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 9;

const sortText: Record<string, string> = {
  heat: "热度优先",
  newest: "最新上映优先",
  oldest: "年代最早优先",
};

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type") ?? "all";

  const [filters, setFilters] = useState<FilterState>({
    category: typeParam,
    year: "all",
    director: "all",
    sort: "heat",
  });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilters((prev) => (prev.category === typeParam ? prev : { ...prev, category: typeParam }));
  }, [typeParam]);

  const results = useMemo(() => applyFilters(filters), [filters]);
  const shown = results.slice(0, visible);
  const currentCategory = categories.find((item) => item.key === filters.category);

  useDocumentMeta(
    `${currentCategory ? currentCategory.name : "全部影片"}电影推荐 - 三级片影视大全`,
    currentCategory
      ? `${currentCategory.name}片单共收录 ${results.length} 部影片：${currentCategory.desc} 支持按年份、导演与热度筛选，快速找到想看的三级片。`
      : "三级片影视大全分类页，收录经典三级片、最新上映与导演专题片单，支持按类型、年份与热度筛选影片。",
    `三级片分类,${currentCategory ? currentCategory.name : "全部影片"},三级片推荐,三级片片单,三级片剧情`
  );

  const updateFilters = (patch: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setVisible(PAGE_SIZE);
    if (patch.category !== undefined) {
      setSearchParams(patch.category === "all" ? {} : { type: patch.category }, { replace: true });
    }
  };

  const resetFilters = () => {
    setFilters({ category: "all", year: "all", director: "all", sort: "heat" });
    setVisible(PAGE_SIZE);
    setSearchParams({}, { replace: true });
  };

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-300 bg-background-100 pt-16 md:pt-[72px]">
        <div className="mx-auto w-full max-w-[1440px] px-4 pb-10 pt-8 md:px-8 md:pb-12 md:pt-10 lg:px-12">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-500">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category" className="transition-colors hover:text-accent-500">
              分类
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{currentCategory ? currentCategory.name : "全部影片"}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                {currentCategory ? currentCategory.name : "全部影片分类"}
              </h1>
              <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-foreground-600">
                {currentCategory
                  ? currentCategory.desc
                  : "按题材、年代与创作者整理的三级片片单。左侧筛选栏可组合子分类、年份与导演，结果会即时更新。"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-600">
                共 {results.length} 部
              </span>
              <span className="rounded-full border border-accent-500/40 px-3 py-1.5 font-label text-xs text-accent-500">
                {sortText[filters.sort]}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr] lg:gap-10">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-300 bg-background-100 p-5">
              <FilterPanel
                filters={filters}
                onChange={updateFilters}
                onReset={resetFilters}
                resultCount={results.length}
              />
            </div>
          </aside>

          <div>
            <div className="mb-5 flex items-center justify-between gap-3 lg:hidden">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 font-label text-sm text-foreground-800"
              >
                <i className="ri-filter-3-line" />
                筛选条件
              </button>
              <span className="font-label text-xs text-foreground-500">共 {results.length} 部影片</span>
            </div>

            <div className="mb-5 hidden flex-wrap items-center gap-2 lg:flex">
              <span className="font-label text-xs text-foreground-500">已选：</span>
              <span className="rounded-full bg-background-200 px-3 py-1 font-label text-xs text-foreground-700">
                {currentCategory ? currentCategory.name : "全部影片"}
              </span>
              {filters.year !== "all" && (
                <span className="rounded-full bg-background-200 px-3 py-1 font-label text-xs text-foreground-700">
                  {filters.year} 年
                </span>
              )}
              {filters.director !== "all" && (
                <span className="rounded-full bg-background-200 px-3 py-1 font-label text-xs text-foreground-700">
                  {filters.director}
                </span>
              )}
            </div>

            {shown.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 xl:grid-cols-4">
                  {shown.map((film, index) => (
                    <FilmCard key={film.id} film={film} index={index} />
                  ))}
                </div>

                <div className="mt-10 flex flex-col items-center gap-3">
                  <p className="font-label text-xs text-foreground-500">
                    已展示 {shown.length} / {results.length} 部
                  </p>
                  <div className="h-1 w-40 overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full bg-accent-500 transition-all duration-500"
                      style={{ width: `${Math.round((shown.length / results.length) * 100)}%` }}
                    />
                  </div>
                  {shown.length < results.length && (
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="mt-2 flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 font-label text-sm text-foreground-950 transition-colors hover:bg-primary-600"
                    >
                      加载更多影片
                      <i className="ri-arrow-down-line" />
                    </button>
                  )}
                  {shown.length >= results.length && results.length > PAGE_SIZE && (
                    <p className="mt-1 font-label text-xs text-foreground-500">已经到底了，试试切换筛选条件</p>
                  )}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-background-300 bg-background-100 px-6 py-20 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-file-search-line text-2xl" />
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">暂无符合条件的影片</h2>
                <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-foreground-600">
                  当前的筛选组合没有匹配到作品，试着放宽年份或切换子分类。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 font-label text-sm text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div
            className="absolute inset-0 bg-background-50/80 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[86vh] overflow-y-auto rounded-t-2xl border-t border-background-300 bg-background-100 p-5">
            <div className="mb-5 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选影片</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={updateFilters}
              onReset={resetFilters}
              resultCount={results.length}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 font-label text-sm font-medium text-background-50"
            >
              查看 {results.length} 部结果
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}